import React, { useCallback, useRef, useState } from 'react';

import { isEqual } from 'lodash-es';
import { useIntl } from 'react-intl';

import { ILocation } from '@rbi-ctg/frontend';
import ChangeRegionModal from 'components/language-selector-modal';
import Picture from 'components/picture';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import { CustomEventNames, logNavigationClick } from 'state/amplitude';
import { useLocale } from 'state/intl';
import { FormValidationState } from 'utils/form';
import { IPlaceAddress } from 'utils/geolocation';
import logger from 'utils/logger';
import { convertCountryStringToIsoCountryCode2Char } from 'utils/rbi-common';

import { ModalContent } from '../styled';
import { IDeliveryAddressFormProps, IPhoneAddressValidation } from '../types';

import { DeliveryAddressFormFooter } from './delivery-address-form-footer';
import { DeliveryAddressFormHeader } from './delivery-address-form-header';
import { DeliveryAddressFormInput } from './delivery-address-form-input';
import { AdditionalInformation, DeliveryLogoContainer } from './styled';

export const isCompleteAddress = (address: IPlaceAddress): boolean => {
  const { addressLine1, city, state, zip } = address;
  return !!(addressLine1 && city && state && zip);
};

export const DeliveryAddressForm: React.FC<React.PropsWithChildren<IDeliveryAddressFormProps>> = ({
  instructions,
  user,
  setCreateAddressView,
  getDetailsPlaceData,
  showDeliveryAddresses,
  setAddress,
  isLoading,
}) => {
  const { formatMessage } = useIntl();
  const { featureDisclaimers } = useFeatureDisclaimers();
  const { deliveryLogo } = featureDisclaimers || {};
  const { region } = useLocale();
  const unitRef = useRef<HTMLInputElement>(null);
  const deliveryAddressInputRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState('');
  const [placeId, setPlaceId] = useState('');

  const [validation, setValidation] = useState<IPhoneAddressValidation>({
    phoneNumber: FormValidationState.PRISTINE,
    address: FormValidationState.PRISTINE,
  });
  const [addressComponents, setAddressComponents] = useState<IPlaceAddress | null>(null);
  const [coordinates, setCoordinates] = useState<ILocation | undefined>();
  const [showChangeRegionModal, setShowChangeRegionModal] = useState<boolean>(false);

  const [addressString, setAddressString] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState(instructions || '');

  const phoneNumber = user?.details?.phoneNumber || '';

  const getAddressRegion = useCallback(
    () => convertCountryStringToIsoCountryCode2Char(addressComponents?.country!),
    [addressComponents]
  );

  const changeRegionModalRegionFilter = useCallback(
    (regionForFilter: string) => {
      return getAddressRegion() === regionForFilter;
    },
    [getAddressRegion]
  );

  const handleSubmit = async () => {
    setMessage('');

    if (!addressComponents || validation.address === FormValidationState.INVALID) {
      setMessage(
        logUserErrorMessage({
          message: formatMessage({ id: 'addressError' }),
          severity: LogUserErrorSeverity.Recoverable,
          context: {
            moreInfo: 'address-invalid',
          },
        })
      );

      logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
        error: 'Invalid Address',
      });

      if (deliveryAddressInputRef.current) {
        deliveryAddressInputRef.current.focus();
      }
      return;
    }

    // track attempt to deliver
    logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE);

    setAddress({
      address: addressComponents,
      deliveryInstructions,
      phoneNumber,
      coordinates,
      placeId,
    });
  };

  const handleSetAddress = useCallback(
    (placeId: string) => {
      getDetailsPlaceData(placeId)
        .then(placeData => {
          if (!isEqual(placeData.address, addressComponents)) {
            setDeliveryInstructions('');
          }

          if (!placeData.address.addressLine1) {
            setValidation({ ...validation, address: FormValidationState.INVALID });
            setMessage(
              logUserErrorMessage({
                message: formatMessage({ id: 'addressError' }),
                severity: LogUserErrorSeverity.Recoverable,
                context: {
                  moreInfo: 'address-line-1-missing',
                },
              })
            );
            return;
          }

          setCoordinates(placeData.coordinates);
          setAddressComponents(placeData.address);
        })
        .catch(error => {
          logger.error({ error, message: 'Error using selected address from predictions' });
        });
    },
    [addressComponents, formatMessage, getDetailsPlaceData, validation]
  );

  return (
    <>
      <ModalContent>
        <DeliveryAddressFormHeader />
        <DeliveryAddressFormInput
          onHandleSetAddress={handleSetAddress}
          setAddressString={setAddressString}
          setPlaceId={setPlaceId}
          setValidation={setValidation}
          setMessage={setMessage}
          setAddressComponents={setAddressComponents}
          unitRef={unitRef}
          validation={validation}
          setCoordinates={setCoordinates}
          addressString={addressString}
          setValue={setAddressString}
          message={message}
          deliveryAddressInputRef={deliveryAddressInputRef}
          data-private
          aria-invalid={!!message}
        />
        {!!message && (
          <VisuallyHidden
            role="alert"
            accessibilityLabel={formatMessage({ id: 'thereIsAnErrorInThisForm' })}
          />
        )}
        <AdditionalInformation>
          {formatMessage({ id: 'additionalAddressInformation' })}
        </AdditionalInformation>
        <DeliveryAddressFormFooter
          submitAddress={handleSubmit}
          setCreateAddressView={setCreateAddressView}
          showDeliveryAddresses={showDeliveryAddresses}
          isDisabled={!addressComponents?.addressLine1 || !!message}
          isLoading={isLoading}
        />
        {deliveryLogo?.locale && (
          <DeliveryLogoContainer>
            <Picture image={deliveryLogo?.locale} alt={formatMessage({ id: 'deliveryLogoAlt' })} />
          </DeliveryLogoContainer>
        )}
      </ModalContent>
      {showChangeRegionModal && (
        <ChangeRegionModal
          redirectToCurrentLocation
          heading={formatMessage({ id: 'deliveryChangeRegionHeading' })}
          regionFilter={changeRegionModalRegionFilter}
          onModalDismiss={() => setShowChangeRegionModal(false)}
          primaryText={formatMessage(
            { id: 'deliveryChangeRegionPrimaryText' },
            { userRegion: region, restaurantRegion: getAddressRegion() }
          )}
          secondaryText={formatMessage({ id: 'locatorChangeRegionSelection' })}
          disclaimer={formatMessage({ id: 'deliveryChangeRegionDisclaimer' })}
        />
      )}
    </>
  );
};
